import pkg from '../../package.json'

export const apiRedirectUrl = process.env.NEXT_PUBLIC_REDIRECT_URL || 'https://app.fundingpips.com'
export const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3000'

export const TRADING_APP_URL = process.env.NEXT_PUBLIC_TRADING_APP_URL

const IS_NON_PRODUCTION =
  !!process.env.NEXT_PUBLIC_NODE_ENV &&
  !!process.env.NEXT_PUBLIC_SENTRY_ENV &&
  process.env.NEXT_PUBLIC_NODE_ENV !== 'production' &&
  process.env.NEXT_PUBLIC_SENTRY_ENV !== 'production'

// Mock mode: requires the flag AND non-production envs, so missing prod envs fail closed.
export const MOCK_ENABLED = process.env.NEXT_PUBLIC_MOCK === 'true' && IS_NON_PRODUCTION

// WS debug tools (activity overlay + render counters): opt-in for non-production envs.
export const WS_DEBUG_ENABLED = process.env.NEXT_PUBLIC_WS_DEBUG === 'true' && IS_NON_PRODUCTION

export const redirectsList = ['/flags', '/dashboards/account_stats']

export const isOldRedirectEnabled = process.env.NEXT_PUBLIC_ENABLE_OLD_CRM_REDIRECTS === 'true'

export const SENTRY_NODE_ENV = process.env.NEXT_PUBLIC_SENTRY_ENV

export const PUBLIC_GOOGLE_MAP_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY || ''
export const PUBLIC_COMMIT_SHA = process.env.NEXT_PUBLIC_COMMIT_SHA || '...'
export const PUBLIC_NODE_ENV = process.env.NEXT_PUBLIC_NODE_ENV || ''

// Datadog
export const PUBLIC_DATADOG_APPLICATION_ID = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID || ''
export const PUBLIC_DATADOG_CLIENT_TOKEN = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || ''
export const PUBLIC_DATADOG_SITE = process.env.NEXT_PUBLIC_DATADOG_SITE || 'datadoghq.eu'
export const PUBLIC_DATADOG_SERVICE = process.env.NEXT_PUBLIC_DATADOG_SERVICE_NAME || 'crm-frontend'
// Datadog is enabled by default (incl. when the env var is unset on qa/prod);
// set NEXT_PUBLIC_DATADOG_DEV_ENABLED="false" to opt out (e.g. local dev).
export const PUBLIC_DATADOG_DEV_ENABLED = process.env.NEXT_PUBLIC_DATADOG_DEV_ENABLED !== 'false'

// Error bus throttle overrides. Each is optional — when unset, the error bus
// falls back to env-tiered defaults (see features/observability/lib/errorThrottle.config.ts).
// NEXT_PUBLIC_* values are inlined at build time, so changing them needs a redeploy.
export const PUBLIC_ERR_BURST_CAP = process.env.NEXT_PUBLIC_ERR_BURST_CAP
export const PUBLIC_ERR_COOLDOWN_MINUTES = process.env.NEXT_PUBLIC_ERR_COOLDOWN_MINUTES
export const PUBLIC_ERR_GLOBAL_MAX = process.env.NEXT_PUBLIC_ERR_GLOBAL_MAX
export const PUBLIC_ERR_GLOBAL_WINDOW_SEC = process.env.NEXT_PUBLIC_ERR_GLOBAL_WINDOW_SEC
// Master kill switch: set NEXT_PUBLIC_ERR_THROTTLE_ENABLED="false" to always send.
export const PUBLIC_ERR_THROTTLE_ENABLED = process.env.NEXT_PUBLIC_ERR_THROTTLE_ENABLED

// Version
const PUBLIC_APP_VERSION = pkg.version

// Datadog release version: must be unique per deploy so uploaded sourcemaps
// match the exact RUM session that produced an error. Combines the app version
// with the commit sha; falls back to the bare version when no real sha is
// present (local/CI placeholder). Datadog versions allow only [a-zA-Z0-9_.-].
const HAS_REAL_COMMIT_SHA = /^[0-9a-f]{7,40}$/i.test(PUBLIC_COMMIT_SHA)
export const PUBLIC_RELEASE_VERSION = (
  HAS_REAL_COMMIT_SHA ? `${PUBLIC_APP_VERSION}-${PUBLIC_COMMIT_SHA}` : PUBLIC_APP_VERSION
).replace(/[^a-zA-Z0-9_.-]/g, '-')

// Page filters persist to the URL (PageFilters -> router.replace / useSearchParams).
export const PAGE_FILTER_KEYS = {
  USERS: 'users',
  TRADING_ACCOUNTS: 'trading_accounts',
  REFERRALS: 'referrals',
  EMAIL_TEMPLATES: 'email_templates',
  PAYOUTS: 'payouts',
  WITHDRAWALS: 'withdrawals',
  COUPONS: 'coupons',
  TASKS: 'tasks',
  SCHEDULED_ACTIONS: 'scheduled_actions',
  IB_PROFILES: 'ib_profiles',
  SALES_AGENTS: 'sales_agents',
  NEWS_ITEMS: 'news_items',
  TEMPLATES: 'templates',
  POLICIES: 'policies',
  DEPOSITS: 'deposits',
  INTERNAL_TRANSFERS: 'internal_transfers',
  GROUPS: 'groups',
  COUNTRIES: 'countries',
  PAYMENT_GATEWAYS: 'payment_provider_gateways',
  RISK_ENGINE_FLAGGED_ACCOUNTS: 'risk_engine_flagged_accounts',
  RISK_ENGINE_SIMILAR_TRADING_ACCOUNTS: 'risk_engine_similar_trading_accounts',
  RISK_ENGINE_SIMILAR_USER_ACCOUNTS: 'risk_engine_similar_user_accounts',
  RISK_ENGINE_TRADING_ACCOUNT_INCIDENTS: 'risk_engine_trading_account_incidents',
  RISK_ENGINE_USER_INCIDENTS: 'risk_engine_user_incidents',
} as const

// Tab filters persist to Redux (TabFilters -> setTabFilters / selectTabFilters).
export const TAB_FILTER_KEYS = {
  USER_IP_ACTIVITIES: 'user_ip_activities',
  BALANCE_TRANSACTIONS: 'balance_transactions',
  OPEN_POSITIONS: 'open_positions',
  CLOSED_POSITIONS: 'closed_positions',
  CHALLENGES: 'challenges',
  USER_TRADING_ACCOUNTS: 'user_trading_accounts',
  TRADING_ACCOUNT_IP_ACTIVITIES: 'trading_account_ip_activities',
  SALES_AGENT_ASSIGNMENTS: 'sales_agent_assignments',
  USER_SALES_AGENT_ASSIGNMENTS: 'user_sales_agent_assignments',
} as const

export const TAB_KEYS = { ...PAGE_FILTER_KEYS, ...TAB_FILTER_KEYS } as const

export const DEFAULT_CURRENCY_USD = 'USD'

export type BrandName = 'fundingpips' | 'fundingticks' | 'tradin'

export const PROJECT_NAME = (process.env.NEXT_PUBLIC_PROJECT_NAME ?? 'fundingpips') as BrandName
export const IS_FUNDING_PIPS = PROJECT_NAME === 'fundingpips'
export const IS_TRADIN = PROJECT_NAME === 'tradin'
// Voiso Omnichannel Workspace iframe. Empty = embedded softphone off.
export const VOISO_EMBED_URL = (IS_TRADIN && process.env.NEXT_PUBLIC_VOISO_EMBED_URL) || ''

export const IS_PRIME_MODE_ENABLED = !IS_TRADIN
export const SESSION_COOKIE_NAME = process.env.NEXT_PUBLIC_SESSION_COOKIE_NAME || `_${PROJECT_NAME}_e34fe2d5`

// Routes that exist only on some brands (matches the backend's per-brand resources); unlisted routes are on every brand.
const BRAND_ROUTES: Record<string, readonly BrandName[]> = {
  '/deposits': ['tradin'],
  '/internal_transfers': ['tradin'],
  '/withdrawals': ['tradin'],
  '/ib_profiles': ['tradin'],
  '/sales_agents': ['tradin'],
  '/coupons': ['fundingpips'],
  '/discount_tiers': ['fundingpips'],
  '/referrals': ['fundingpips'],
}

export const isRouteEnabledForProject = (href: string, brand: BrandName = PROJECT_NAME): boolean =>
  BRAND_ROUTES[href]?.includes(brand) ?? true

const BRAND_LABELS: Record<BrandName, string> = {
  fundingpips: 'FP CRM',
  fundingticks: 'FT CRM',
  tradin: 'Tradin CRM',
}

const BRAND_DASHBOARD_TITLES: Record<BrandName, string> = {
  fundingpips: 'FP Dashboard',
  fundingticks: 'FT Dashboard',
  tradin: 'Tradin Dashboard',
}

const BRAND_FAVICON_PATHS: Record<BrandName, string> = {
  fundingpips: '/favicons/fp_favicon.ico',
  fundingticks: '/favicons/ft_favicon.ico',
  tradin: '/favicons/tradin_favicon.ico',
}

const BRAND_FAVICON_PNG_PATHS: Partial<Record<BrandName, string>> = {
  tradin: '/favicons/tradin_favicon.png',
}

// --- Generic getter with fallback to fundingpips ---
function brandLookup<T>(map: Record<BrandName, T>, brand: BrandName = PROJECT_NAME): T {
  return map[brand] ?? map.fundingpips
}

export const getBrandLabel = (brand?: BrandName) => brandLookup(BRAND_LABELS, brand)
export const getBrandDashboardTitle = (brand?: BrandName) => brandLookup(BRAND_DASHBOARD_TITLES, brand)
export const getBrandFavicon = (brand?: BrandName) => brandLookup(BRAND_FAVICON_PATHS, brand)
export const getBrandFaviconPng = (brand?: BrandName) => BRAND_FAVICON_PNG_PATHS[brand ?? PROJECT_NAME]

// Yes/No select options for boolean filters (sent as 'true' / 'false').
export const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]
